import { FC } from 'react';
import { Modal, TextField } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { useSendPushNotification } from 'store/admin-notifications';

interface Values {
  employeeId: string;
  title: string;
  body: string;
  deeplink: string;
}

const INITIAL_VALUES: Values = {
  employeeId: '',
  title: '',
  body: '',
  deeplink: '',
};

interface Props extends ModalProps {}

export const SendPushNotificationModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const { mutate: sendPushNotification, isLoading: isSending } =
    useSendPushNotification();

  const onSubmit: SubmitHandler<Values> = (values) => {
    sendPushNotification(
      {
        employeeId: values.employeeId,
        title: values.title,
        body: values.body,
        deeplink: values.deeplink,
      },
      {
        onSuccess: () => {
          predefinedToasts.success.added();
          closeModal();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        // TODO: trans
        title="Push notifications"
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('buttons.send'),
            type: 'submit',
            disabled: !isDirty || isSending,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="employeeId"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('entity_type.employee')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
          <Controller
            name="title"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('common.name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
          <Controller
            name="body"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('integrations.logMessage')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
          <Controller
            name="deeplink"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('shop.vouchers.redeem.url')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
